import { useCallback, useState } from 'react';
import {
  ApiErrorResponse,
  excelTranslations,
  GetApiErrorMessage,
  InputUpdateResponseType,
  useAlertContext,
  ValidationMessagesMap,
  InputCommentsUpdateResponseType,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import { useInputDataDetailsStoreValue } from './InputDataDetailsContext';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './useInputDataActions.translations.json';

const { addTranslations, t, exists } = i18nNsFactory(
  'input-data-mutation-hook',
);
addTranslations(translations);
addTranslations(excelTranslations);

export const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

export function useInputDataActions(onReload?: () => Promise<void>) {
  const [, setErrors] = useInputDataDetailsStoreValue('mutationErrors');
  const [savedVersion, setSavedVersion] =
    useInputDataDetailsStoreValue('savedVersion');
  const { resetAlert, setSuccess } = useAlertContext();
  const [isLoading, setIsLoading] = useState(false);

  const onError = useCallback(
    (apiErrorResponse: ApiErrorResponse, errorType?: string) => {
      setIsLoading(false);
      resetAlert();
      setErrors({
        errors: ValidationMessagesMap.fromApiErrors(
          apiErrorResponse.response?.data.errors || [],
          translateMutationError,
        ),
        type: errorType,
      });
    },
    [resetAlert, setErrors],
  );

  const onSuccess = useCallback(
    async (
      data: InputUpdateResponseType | InputCommentsUpdateResponseType | void,
    ) => {
      setIsLoading(false);
      setErrors(undefined);
      setSuccess();
      if (data) {
        setSavedVersion({
          ...savedVersion,
          ...(data as InputUpdateResponseType &
            InputCommentsUpdateResponseType),
        });
      }

      await onReload?.();
    },
    [setErrors, onReload, setSuccess, setSavedVersion, savedVersion],
  );

  const onExcelUploadSuccess = useCallback(() => {
    setIsLoading(false);
    setErrors(undefined);
  }, [setErrors]);

  const onLoad = useCallback(() => {
    resetAlert();
    setIsLoading(true);
    setErrors(undefined);
  }, [resetAlert, setErrors]);

  const onClear = useCallback(() => {
    resetAlert();
    setIsLoading(false);
    setErrors(undefined);
  }, [resetAlert, setErrors]);

  return {
    onClear,
    onLoad,
    onError,
    onSuccess,
    onExcelUploadSuccess,
    isLoading,
    setIsLoading,
  };
}
